import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Tabs,
  Tab,
  Divider,
  Checkbox,
  Paper,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
} from "@mui/material";
import { EditAvatar } from "../crew-management/components/EditProfilePicture";
// import { Dayjs } from "dayjs";
import {
  Edit as EditIcon,
  Lock as LockIcon,
  PersonOff as DeactivateIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  OpenInNew as OpenInNewIcon,
  AccessTime as TimeIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  subDays,
  startOfWeek,
  startOfMonth,
  isAfter,
  // isBefore,
  isSameDay,
  startOfYear,
} from "date-fns";

import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import DataGrid from "../../components/common/CustomeDataGrid";

import { TextField } from "@mui/material";

import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import { useParams, useNavigate } from "react-router-dom";
import type { User } from "../../store/slice/user-management/types";
import EditUserDialog from "./EditUserDialog";
import { AddCrewMemberDialog } from "../crew-management/components";
import ProfessionalDevelopmentTab from "./components/ProfessionalDevelopmentTab";
import EmploymentHistoryCalendar from "./components/EmploymentHistoryCalendar";
import PersonalityTestTab from "./components/PersonalityTestTab";
import { tokens } from "../../theme";
import type {
  AddCrewFormProps,
  RoleSkillTool,
} from "../crew-management/types/add-crew-member";
import apiService from "../../service/Api";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../store/hooks";
import {
  fetchContentTypes,
  fetchGroups,
  fetchPermissions,
} from "../../store/slice/rolesAndPermissions/thunks";
import { format } from "date-fns";
import AddReferralPartnerModal from "../referral-program/components/ReferralPartnerModal";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/permissions";

export interface ModulePermission {
  id: string; // REQUIRED for DataGrid
  module: string; // "booking.booking"
  app_label: string;
  model: string;
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
  // Per-verb "locked" flags: the permission is granted by a role/group and
  // therefore cannot be toggled off on this user (Django has no per-user deny).
  viewLocked: boolean;
  addLocked: boolean;
  editLocked: boolean;
  deleteLocked: boolean;
}

type PermissionAction = "view" | "add" | "edit" | "delete";

// Django permission verb for each column (edit maps to Django's "change").
const PERMISSION_VERB: Record<PermissionAction, string> = {
  view: "view",
  add: "add",
  edit: "change",
  delete: "delete",
};

const buildPermissionString = (
  row: ModulePermission,
  action: PermissionAction,
): string => `${row.app_label}.${PERMISSION_VERB[action]}_${row.model}`;

// interface Role {
//   id: string;
//   name: string;
//   description: string;
//   checked: boolean;
// }

interface ActivityLog {
  id: number;
  user: number;
  user_email: string;
  action: string;
  action_display: string;
  table_name: string;
  record_id: number | null;
  timestamp: string;
  ip_address: string;
  additional_info: string;
  created_at: string;
}

type EmploymentType = "Full time" | "intern" | "Pixel Pilot" | "Part time" | "contract";

interface EmploymentHistory {
  id: number;
  position: string;
  department: string;
  employment_type: EmploymentType;
  start_date: string;
  end_date: string | null;
  is_current: boolean;
}

interface Course {
  id: number;
  course_name: string;
  institution: string;
  start_date: string;
  end_date: string | null;
  status: "In Progress" | "Completed" | "Dropped";
}

type TShirtSize = "XS" | "S" | "M" | "L" | "XL" | "XXL" | "XXXL";

export default function UserProfile() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canEditUser = hasPermission(PERMISSIONS.USER_MANAGEMENT_CHANGE);
  const [userData, setUserData] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState<boolean>(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [crew_member_id, setCrew_member_id] = useState<number | null>(null);

  const [isCrewMember, setIsCrewMember] = useState<boolean>(false);

  // Add Crew Member Dialog State
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const preferencesSource = userData?.service_preferences;
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [isReferralPartner, setIsReferralPartner] = useState(false);
  const [referralPartnerId, setReferralPartnerId] = useState<number | null>(
    null,
  );
  const [formData, setFormData] = useState<AddCrewFormProps>({
    existingUser: true,
    user_id: userData?.id || null,
    first_name: userData?.first_name || "",
    last_name: userData?.last_name || "",
    email: userData?.email || "",
    username: userData?.username || "",
    password: "tilapia123",
    phone: userData?.phone_number || "",
    role: userData?.role || "",
    organization: userData?.organization || "",
    street_address: userData?.street_address || "",
    city: userData?.city || "",
    state: userData?.state_province || "",
    postal_code: userData?.postal_code || "",
    residence_country: userData?.residence_country || "",
    emergency_contact: userData?.emergency_contact || "",
    emergency_contact_phone: userData?.emergency_contact_phone || "",
    date_of_birth: userData?.date_of_birth || "",
    occupation: userData?.occupation || "",
    marital_status: userData?.marital_status || null,
    national_id: userData?.national_id || "",
    roles: [],
    skills: [],
    tools: [],
    preferences: Array.isArray(preferencesSource)
      ? preferencesSource.join(", ")
      : (preferencesSource ?? ""),
    experience_years: 0,
  });
  const [selectedCountry, setSelectedCountry] = useState<
    string | null | undefined
  >(userData?.residence_country || null);
  const [selectedRoles, setSelectedRoles] = useState<RoleSkillTool[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<RoleSkillTool[]>([]);
  const [selectedTools, setSelectedTools] = useState<RoleSkillTool[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Professional Development State
  const [employmentHistory, setEmploymentHistory] = useState<EmploymentHistory[]>([
    {
      id: 1,
      position: "Intern Developer",
      department: "Engineering",
      employment_type: "intern",
      start_date: "2024-06-01",
      end_date: "2024-12-31",
      is_current: false,
    },
    {
      id: 2,
      position: "Junior Developer",
      department: "Engineering",
      employment_type: "Full time",
      start_date: "2025-01-15",
      end_date: "2025-12-31",
      is_current: false,
    },
    {
      id: 3,
      position: "Pixel Pilot",
      department: "Operations",
      employment_type: "Pixel Pilot",
      start_date: "2026-01-01",
      end_date: "2026-06-30",
      is_current: false,
    },
    {
      id: 4,
      position: "Senior Developer",
      department: "Engineering",
      employment_type: "Full time",
      start_date: "2026-07-01",
      end_date: null,
      is_current: true,
    },
  ]);

  const [courses, setCourses] = useState<Course[]>([
    {
      id: 1,
      course_name: "Advanced React Development",
      institution: "Tech Academy",
      start_date: "2023-01-15",
      end_date: "2023-06-30",
      status: "Completed",
    },
    {
      id: 2,
      course_name: "Cloud Architecture",
      institution: "Cloud University",
      start_date: "2024-01-01",
      end_date: null,
      status: "In Progress",
    },
  ]);
  const [tShirtSize, setTShirtSize] = useState<TShirtSize>("M");

  type ClothingSize = "XS" | "S" | "M" | "L" | "XL" | "XXL" | "XXXL";

  interface ClothingSizes {
    shirt: ClothingSize;
    trousers: ClothingSize;
    shoe: number;
    jacket: ClothingSize;
  }

  const [clothingSizes, setClothingSizes] = useState<ClothingSizes>({
    shirt: "M",
    trousers: "M",
    shoe: 42,
    jacket: "M",
  });

  // Personality Test State
  type PersonalityType = "INTJ" | "INTP" | "ENTJ" | "ENTP" | "INFJ" | "INFP" | "ENFJ" | "ENFP" | "ISTJ" | "ISFJ" | "ESTJ" | "ESFJ" | "ISTP" | "ISFP" | "ESTP" | "ESFP";

  interface PersonalityTestResult {
    id: number;
    personality_type: PersonalityType;
    summary: string | null;
    test_date: string;
    pdf_file: File | null;
  }

  const [personalityTest, setPersonalityTest] = useState<PersonalityTestResult | null>(null);

  type TimeFilter =
    | "all"
    | "today"
    | "last_3_days"
    | "this_week"
    | "this_month"
    | "custom"
    | "this_year";

  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  // const [customFromDate, setCustomFromDate] = useState<Dayjs | null>(null);
  // const [customToDate, setCustomToDate] = useState<Dayjs | null>(null);
  const [activityFilter, setActivityFilter] = useState("All");
  const [lastLogIn, setLastLogIn] = useState("");
  const [effectivePermissions, setEffectivePermissions] = useState<string[]>(
    [],
  );
  const [groupPermissions, setGroupPermissions] = useState<string[]>([]);
  const [isTargetSuperuser, setIsTargetSuperuser] = useState<boolean>(false);
  // Permission strings currently being saved, to disable those cells.
  const [savingPermissions, setSavingPermissions] = useState<Set<string>>(
    new Set(),
  );
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [savingRoles, setSavingRoles] = useState<boolean>(false);
  const [permissionSearch, setPermissionSearch] = useState<string>("");

  const { contentTypes, groups } = useSelector(
    (state: RootState) => state.rolesAndPermissions,
  );

  const modulePermissions: ModulePermission[] = React.useMemo(() => {
    if (!Array.isArray(contentTypes)) {
      return [];
    }

    const map = new Map<string, ModulePermission>();

    // Initialize all modules
    contentTypes.forEach((ct) => {
      const key = `${ct.app_label} / ${ct.model}`;

      map.set(key, {
        id: key,
        module: key,
        app_label: ct.app_label,
        model: ct.model,
        view: false,
        add: false,
        edit: false,
        delete: false,
        viewLocked: false,
        addLocked: false,
        editLocked: false,
        deleteLocked: false,
      });
    });

    const applyPerm = (perm: string, asLock: boolean) => {
      const [appLabel, codename] = perm.split(".");
      if (!appLabel || !codename) return;
      const separatorIndex = codename.indexOf("_");
      if (separatorIndex === -1) return;
      const verb = codename.slice(0, separatorIndex);
      const model = codename.slice(separatorIndex + 1);
      const key = `${appLabel} / ${model}`;
      const row = map.get(key);
      if (!row) return;

      if (verb === "view") {
        if (asLock) row.viewLocked = true;
        else row.view = true;
      } else if (verb === "add") {
        if (asLock) row.addLocked = true;
        else row.add = true;
      } else if (verb === "change") {
        if (asLock) row.editLocked = true;
        else row.edit = true;
      } else if (verb === "delete") {
        if (asLock) row.deleteLocked = true;
        else row.delete = true;
      }
    };

    // Effective permissions drive the checked state.
    effectivePermissions.forEach((perm) => applyPerm(perm, false));
    // Role/group permissions drive the locked state. For a superuser every
    // permission is effectively locked.
    if (isTargetSuperuser) {
      map.forEach((row) => {
        row.viewLocked = true;
        row.addLocked = true;
        row.editLocked = true;
        row.deleteLocked = true;
      });
    } else {
      groupPermissions.forEach((perm) => applyPerm(perm, true));
    }

    return Array.from(map.values());
  }, [contentTypes, effectivePermissions, groupPermissions, isTargetSuperuser]);

  const filteredModulePermissions = React.useMemo(() => {
    const term = permissionSearch.trim().toLowerCase();
    if (!term) return modulePermissions;
    return modulePermissions.filter((m) =>
      m.module.toLowerCase().includes(term),
    );
  }, [modulePermissions, permissionSearch]);

  const addCrewMemberUser = React.useMemo(() => {
    if (!userData) return null;

    return {
      id: userData.id,
      email: userData.email || "",
      username: userData.username || "",
      first_name: userData.first_name || "",
      last_name: userData.last_name || "",
      user_type: "company_user" as const,
      country: userData.country ?? null,
      phone_number: userData.phone_number ?? null,
      role: (["admin", "client", "crew", "funeral_director", "staff"].includes(
        userData.role,
      )
        ? userData.role
        : "crew") as "admin" | "client" | "crew" | "funeral_director" | "staff",
      organization: userData.organization ?? null,
      is_verified: userData.is_verified ?? false,
      profile_picture: userData.profile_picture_url ?? null,
      street_address: userData.street_address ?? null,
      city: userData.city ?? null,
      state_province: userData.state_province ?? null,
      postal_code: userData.postal_code ?? null,
      residence_country: userData.residence_country ?? null,
      date_of_birth: userData.date_of_birth ?? null,
      occupation: userData.occupation ?? null,
      marital_status: "single" as const,
      national_id: userData.national_id ?? null,
      emergency_contact: userData.emergency_contact ?? null,
      emergency_contact_phone: userData.emergency_contact_phone ?? null,
      assignment: userData.assignment ?? null,
      service_preferences:
        typeof userData.service_preferences === "string"
          ? userData.service_preferences
          : Array.isArray(userData.service_preferences)
            ? userData.service_preferences.join(", ")
            : null,
      company: userData.company ?? null,
      company_branch: userData.company_branch ?? null,
      is_2fa_enabled: userData.is_2fa_enabled ?? false,
      is_active: userData.is_active ?? false,
      user_timezone: userData.user_timezone ?? null,
    };
  }, [userData]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const columns: GridColDef<ActivityLog>[] = [
    {
      field: "timestamp",
      headerName: "Time",
      minWidth: 160,
      flex: 1,
      renderCell: (params) => (
        <Box>
          {format(new Date(params.row.timestamp), "dd MMM yyyy hh:mm a")}
        </Box>
      ),
    },
    {
      field: "action_display",
      headerName: "Action",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "additional_info",
      headerName: "Description",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "table_name",
      headerName: "Table Name",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "ip_address",
      headerName: "IP Address",
      minWidth: 160,
      flex: 1,
    },
  ];

  const handleTogglePermission = React.useCallback(
    async (
      row: ModulePermission,
      action: PermissionAction,
      granted: boolean,
    ) => {
      if (!id) return;
      const permString = buildPermissionString(row, action);

      setSavingPermissions((prev) => new Set(prev).add(permString));
      // Optimistic update of the effective set (drives the checked state).
      setEffectivePermissions((prev) =>
        granted
          ? Array.from(new Set([...prev, permString]))
          : prev.filter((p) => p !== permString),
      );

      try {
        await apiService.post(
          `/api/authentication/users/${id}/set-permission/`,
          { permission: permString, granted },
        );
        toast.success(granted ? "Permission granted" : "Permission removed");
      } catch (err) {
        // Revert on failure.
        setEffectivePermissions((prev) =>
          granted
            ? prev.filter((p) => p !== permString)
            : Array.from(new Set([...prev, permString])),
        );
        const errorDetail = (
          err as { response?: { data?: { detail?: string } } }
        )?.response?.data?.detail;
        toast.error(errorDetail || "Failed to update permission");
      } finally {
        setSavingPermissions((prev) => {
          const next = new Set(prev);
          next.delete(permString);
          return next;
        });
      }
    },
    [id],
  );

  const renderPermissionCell = (
    row: ModulePermission,
    action: PermissionAction,
  ) => {
    const checked = row[action];
    const locked =
      row[`${action}Locked` as keyof ModulePermission] === true;
    const saving = savingPermissions.has(buildPermissionString(row, action));
    // A locked (role-granted) permission cannot be toggled off here; neither
    // can anything if the viewer lacks edit rights or the target is a superuser.
    const disabled = !canEditUser || isTargetSuperuser || locked || saving;

    return (
      <Tooltip
        title={
          locked
            ? "Granted by a role. Change it on the role to affect this user."
            : ""
        }
        disableHoverListener={!locked}
      >
        <span>
          <Checkbox
            checked={checked}
            disabled={disabled}
            onChange={(e) =>
              handleTogglePermission(row, action, e.target.checked)
            }
          />
        </span>
      </Tooltip>
    );
  };

  const permission_columns: GridColDef<ModulePermission>[] = [
    {
      field: "module",
      headerName: "Module",
      flex: 1.5,
      sortable: true,
    },
    {
      field: "view",
      headerName: "View",
      width: 100,
      renderCell: (params) => renderPermissionCell(params.row, "view"),
    },
    {
      field: "add",
      headerName: "Add",
      width: 100,
      renderCell: (params) => renderPermissionCell(params.row, "add"),
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 100,
      renderCell: (params) => renderPermissionCell(params.row, "edit"),
    },
    {
      field: "delete",
      headerName: "Delete",
      width: 100,
      renderCell: (params) => renderPermissionCell(params.row, "delete"),
    },
  ];

  // Handlers for AddCrewMemberDialog (minimal, can be expanded as needed)
  const handleAddCrewMember = async () => {
    setFormLoading(true);
    try {
      // Example API call, adjust endpoint and payload as needed
      const res = await apiService.post<User>(
        "/api/crews/crew-members/full-create/",
        formData,
      );
      toast.success("Crew member added successfully");
      setCrew_member_id(res.data.id);
      setAddMemberOpen(false);
      setIsCrewMember(true);
      // Optionally refresh user/crew data here
    } catch (err) {
      console.error("Failed to add crew member", err);
      toast.error("Failed to add crew member");
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddReferralPartner = async (formData: {
    businessName: string;
    organisationCategory: string;
    agreeTerms: boolean;
  }) => {
    try {
      const res = await apiService.post<{
        referral_partner: { id: number };
        message: string;
      }>("/api/refferal-program/register/", {
        user_id: userData?.id,
        first_name: userData?.first_name,
        last_name: userData?.last_name,
        email: userData?.email,
        phone_number: userData?.phone_number,
        business_name: formData.businessName,
        category: formData.organisationCategory,
        agreed_to_terms: formData.agreeTerms,
      });
      toast.success("User added as referral partner successfully");
      setIsReferralPartner(true);
      setReferralPartnerId(res.data.referral_partner.id);
      setReferralModalOpen(false);
    } catch (err) {
      const errorDetail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error(errorDetail || "Failed to add as referral partner");
    }
  };
  const handleInputChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handlePhoneChange = (value: string) => {
    setFormData((prev) => ({ ...prev, phone: value }));
  };
  const handleEmergencyContactChange = (value: string) => {
    setFormData((prev) => ({ ...prev, emergency_contact_phone: value }));
  };
  const onCountryChange = (country: string | null) => {
    setSelectedCountry(country);
    setFormData((prev) => ({ ...prev, residence_country: country || "" }));
  };
  const onRolesChange = (roles: RoleSkillTool[]) => {
    setSelectedRoles(roles);
    setFormData((prev) => ({ ...prev, roles: roles.map((r) => r.id) }));
  };
  const onSkillsChange = (skills: RoleSkillTool[]) => {
    setSelectedSkills(skills);
    setFormData((prev) => ({ ...prev, skills: skills.map((s) => s.id) }));
  };
  const onToolsChange = (tools: RoleSkillTool[]) => {
    setSelectedTools(tools);
    setFormData((prev) => ({ ...prev, tools: tools.map((t) => t.id) }));
  };
  const resetForm = () => {
    const preferencesSource = userData?.service_preferences;
    setFormData({
      existingUser: true,
      user_id: userData?.id || null,
      first_name: userData?.first_name || "",
      last_name: userData?.last_name || "",
      email: userData?.email || "",
      username: userData?.username || "",
      password: "tilapia123",
      phone: userData?.phone_number || "",
      role: userData?.role || "",
      organization: userData?.organization || "",
      street_address: userData?.street_address || "",
      city: userData?.city || "",
      state: userData?.state_province || "",
      postal_code: userData?.postal_code || "",
      residence_country: userData?.residence_country || "",
      emergency_contact: userData?.emergency_contact || "",
      emergency_contact_phone: userData?.emergency_contact_phone || "",
      date_of_birth: userData?.date_of_birth || "",
      occupation: userData?.occupation || "",
      marital_status: userData?.marital_status || null,
      national_id: userData?.national_id || "",
      roles: [],
      skills: [],
      tools: [],
      preferences: Array.isArray(preferencesSource)
        ? preferencesSource.join(", ")
        : (preferencesSource ?? ""),
      experience_years: 0,
    });
    setSelectedCountry(userData?.residence_country || null);
    setSelectedRoles([]);
    setSelectedSkills([]);
    setSelectedTools([]);
  };
  const removeRole = (roleId: number) => {
    setSelectedRoles((prev) => prev.filter((r) => r.id !== roleId));
    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.filter((id) => id !== roleId),
    }));
  };
  const removeSkill = (skillId: number) => {
    setSelectedSkills((prev) => prev.filter((s) => s.id !== skillId));
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((id) => id !== skillId),
    }));
  };
  const removeTool = (toolId: number) => {
    setSelectedTools((prev) => prev.filter((t) => t.id !== toolId));
    setFormData((prev) => ({
      ...prev,
      tools: prev.tools.filter((id) => id !== toolId),
    }));
  };

  const checkUser = React.useCallback(async () => {
    try {
      const response = await apiService.get<{ results: Array<{ id: number }> }>(
        `/api/crews/crew-members/?user=${id}`,
      );

      const crewmember = response.data?.results[0];

      if (crewmember) {
        setIsCrewMember(true);
        setCrew_member_id(crewmember.id);
      } else {
        setIsCrewMember(false);
      }
    } catch (err) {
      console.error("Error :", err);
    }
  }, [id]);

  const checkReferralPartner = React.useCallback(async () => {
    try {
      // 1. Fetch the raw un-filtered list from the endpoint
      const response = await apiService.get<{
        results: Array<{
          id: number;
          referral_partner: { id: number };
        }>;
      }>(`/api/refferal-program/refferal-programs/?page_size=1000`);

      const allPrograms = response.data?.results || [];

      // 2. 🎯 Match the correct program dynamically on the frontend
      // We cast both IDs to Strings to guarantee a perfect comparison type match
      const matchingPartner = allPrograms.find(
        (program) => String(program.referral_partner?.id) === String(id),
      );

      if (matchingPartner) {
        setIsReferralPartner(true);
        // 3. Save the correct top-level program ID (e.g., 6, 10, 9, or 11)
        setReferralPartnerId(matchingPartner.id);
      } else {
        setIsReferralPartner(false);
        setReferralPartnerId(null);
      }
    } catch (err) {
      console.error("Error checking referral partner matches:", err);
    }
  }, [id]);
  const fetchLogs = React.useCallback(async () => {
    try {
      setLoading(true);

      interface resI {
        count: number;
        next: string;
        previous: string;
        results: ActivityLog[];
      }

      const response = await apiService.get<resI>(
        `/api/logs/system-log/?user=${id}&page_size=100000`,
      );

      setActivityLogs(response.data.results);

      const last = response.data.results.find(
        (activity) => activity.table_name === "auth_login",
      );

      setLastLogIn(
        last ? format(new Date(last.timestamp), "dd MMM yyyy hh:mm a") : "N/A",
      );
    } catch (err) {
      console.error("Error fetching logs:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const dispatch = useAppDispatch();

  const fetchUserById = React.useCallback(async () => {
    if (!id) return;
    try {
      setUserLoading(true);
      const response = await apiService.get<User>(
        `/api/authentication/users/${id}/`,
      );
      setUserData(response.data);
    } catch (err) {
      console.error("Error fetching user:", err);
      setUserData(null);
    } finally {
      setUserLoading(false);
    }
  }, [id]);

  const fetchEffectivePermissions = React.useCallback(async () => {
    if (!id) return;
    try {
      const response = await apiService.get<
        | string[]
        | {
            permissions: string[];
            group_permissions?: string[];
            is_superuser?: boolean;
          }
      >(`/api/authentication/users/${id}/effective-permissions/`);
      const data = response.data;
      if (Array.isArray(data)) {
        setEffectivePermissions(data);
        setGroupPermissions([]);
        setIsTargetSuperuser(false);
      } else {
        setEffectivePermissions(data.permissions ?? []);
        setGroupPermissions(data.group_permissions ?? []);
        setIsTargetSuperuser(Boolean(data.is_superuser));
      }
    } catch (err) {
      console.error("Error fetching effective permissions:", err);
      setEffectivePermissions([]);
      setGroupPermissions([]);
      setIsTargetSuperuser(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchUserById();
      fetchEffectivePermissions();
      checkUser();
      fetchLogs();
      checkReferralPartner();
    }
    // Ensure roles/permissions catalogs are available even on a direct deep-link
    dispatch(fetchPermissions());
    dispatch(fetchGroups());
    dispatch(fetchContentTypes());
  }, [
    id,
    dispatch,
    fetchUserById,
    fetchEffectivePermissions,
    checkUser,
    fetchLogs,
    checkReferralPartner,
  ]);

  // Sync the editable role selection from the user's assigned groups
  useEffect(() => {
    if (!userData || !Array.isArray(groups)) return;
    const names = (userData.group_names ?? []).map((n) => n.toLowerCase());
    setSelectedGroupIds(
      groups.filter((g) => names.includes(g.name.toLowerCase())).map((g) => g.id),
    );
  }, [userData, groups]);

  const handleToggleGroup = (groupId: number) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((gid) => gid !== groupId)
        : [...prev, groupId],
    );
  };

  const handleSaveRoles = async () => {
    if (!id) return;
    try {
      setSavingRoles(true);
      await apiService.patch(`/api/authentication/users/${id}/`, {
        groups: selectedGroupIds,
      });
      toast.success("Roles updated successfully");
      await fetchUserById();
      await fetchEffectivePermissions();
    } catch (err) {
      const errorDetail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error(errorDetail || "Failed to update roles");
    } finally {
      setSavingRoles(false);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
  };

  const handleEditUser = () => {
    if (userData) {
      setEditDialogOpen(true);
    }
  };

  const handleSaveUser = async (formData: FormData) => {
    try {
      await apiService.patch(`/api/authentication/users/${id}/`, formData);
      toast.success("User updated successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setEditDialogOpen(false);
    } catch (err) {
      let errorMessage = "Failed to update user. Please try again.";
      const errorDetail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      if (errorDetail) {
        errorMessage = errorDetail;
      }
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Update user error:", err);
    }
  };

  const handleDeactivateUser = async () => {
    try {
      await apiService.patch(`/api/authentication/users/${id}/`, {
        is_active: userData?.is_active,
      });
      setUserData((prev) =>
        prev
          ? {
              ...prev,
              is_active: prev.is_active,
            }
          : null,
      );
      toast.success(
        `User ${userData?.is_active ? "deactivated" : "activated"} successfully`,
        {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        },
      );
    } catch (err) {
      let errorMessage = `Failed to ${userData?.is_active ? "deactivate" : "activate"} user. Please try again.`;
      const errorDetail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      if (errorDetail) {
        errorMessage = errorDetail;
      }
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Deactivate/Activate user error:", err);
    }
  };

  const handleResetPassword = async () => {
    try {
      await apiService.post(
        `/api/authentication/forgot-password/`,
        { email: userData?.email },
        true,
      );
      toast.success("Password reset email sent successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (error: unknown) {
      const errorDetail = error as {
        response?: { data?: { detail?: string } };
      };
      console.log(errorDetail);
      toast.error(
        errorDetail?.response?.data?.detail ||
          "Failed to send password reset email. Please try again.",
        {
          position: "top-right",
          autoClose: 5000,
        },
      );
    }
  };

  // No backend fetch, userData is derived from redux state
  // client, funeral_director, staff, admin, crew, support
  // const [roles] = useState<Role[]>([
  //   {
  //     id: "admin",
  //     name: "Admin",
  //     description:
  //       "Full system control. Manages users, roles, pricing plans, platform settings, and sensitive operational data.",
  //     checked: false,
  //   },
  //   {
  //     id: "staff",
  //     name: "Staff",
  //     description:
  //       "Handles client onboarding, scheduling of funeral streams, communication with families, and general operational support.",
  //     checked: false,
  //   },
  //   {
  //     id: "crew",
  //     name: "Crew",
  //     description:
  //       "Responsible for on-site streaming operations, camera setup, audio, internet connectivity, and ensuring a smooth live broadcast.",
  //     checked: false,
  //   },
  //   {
  //     id: "client",
  //     name: "Client",
  //     description:
  //       "Family member or organization that requests and manages a funeral live stream, views analytics, and shares stream access with viewers.",
  //     checked: false,
  //   },
  //   {
  //     id: "funeral_director",
  //     name: "Funeral Director",
  //     description:
  //       "Coordinates funeral services, approves streaming schedules, liaises with families, and ensures services align with funeral arrangements.",
  //     checked: false,
  //   },
  // ]);

  const filteredActivityLogs = React.useMemo(() => {
    const now = new Date();

    return activityLogs.filter((log) => {
      const logDate = new Date(log.timestamp);

      let matchesTime = true;

      switch (timeFilter) {
        case "today":
          matchesTime = isSameDay(logDate, now);
          break;

        case "last_3_days":
          matchesTime = isAfter(logDate, subDays(now, 3));
          break;

        case "this_week":
          matchesTime = isAfter(logDate, startOfWeek(now));
          break;

        case "this_month":
          matchesTime = isAfter(logDate, startOfMonth(now));
          break;

        case "this_year":
          matchesTime = isAfter(logDate, startOfYear(now));
          break;

        // case "custom":
        //   if (customFromDate && customToDate) {
        //     matchesTime =
        //       isAfter(logDate, customFromDate.toDate()) &&
        //       isBefore(logDate, customToDate.toDate());
        //   }
        //   break;
      }
      let acto = activityFilter;
      if (activityFilter.toLowerCase() === "all") {
        acto = "";
      }

      let matchesActivity = true;
      if (acto) {
        matchesActivity = log.action.toLowerCase().includes(acto.toLowerCase());
      }

      return matchesTime && matchesActivity;
    });
  }, [
    activityLogs,
    timeFilter,
    // customFromDate,
    // customToDate,
    activityFilter,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // if (loading) {
  //   return (
  //     <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
  //       <CircularProgress size={40} />
  //     </Box>
  //   );
  // }

  if (userLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !userData) {
    return (
      <Box sx={{ p: 3, minHeight: "100vh" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to Users
        </Button>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          }
        >
          {error || "User not found"}
        </Alert>
      </Box>
    );
  }

  const getStatusColor = (status: string): "success" | "warning" => {
    return status === "Active" ? "success" : "warning";
  };

  const tabContents = [
    // Tab 0: Overview
    <React.Fragment key="overview">
      <Grid container spacing={3} sx={{ mt: 1, ml: 2, mr: 2, py: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ boxShadow: 0 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Contact Information
              </Typography>
              <List disablePadding>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <EmailIcon color="disabled" />
                  </ListItemIcon>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{userData.email}</Typography>
                  </Box>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PhoneIcon color="disabled" />
                  </ListItemIcon>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {userData.phone_number || "N/A"}
                    </Typography>
                  </Box>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CalendarIcon color="disabled" />
                  </ListItemIcon>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Timezone
                    </Typography>
                    <Typography variant="body1">
                      {userData.user_timezone}
                    </Typography>
                  </Box>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PersonIcon color="disabled" />
                  </ListItemIcon>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Access Level
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      {/* {(userData.roles || []).map((role: string) => (
                        <Chip
                          key={role}
                          label={role}
                          color="primary"
                          size="small"
                        />
                      ))} */}
                      <Chip
                        label={userData.role}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Recent Activity
              </Typography>
              <List disablePadding>
                {loading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", my: 2 }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <Box>
                    {activityLogs
                      .slice(0, 5)
                      .map((activity: ActivityLog, index: number) => (
                        <React.Fragment key={index}>
                          <ListItem disablePadding sx={{ py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <TimeIcon color="disabled" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={activity.additional_info}
                              secondary={format(
                                new Date(activity.timestamp),
                                "dd MMM yyyy hh:mm a",
                              )}
                              primaryTypographyProps={{ variant: "body2" }}
                              secondaryTypographyProps={{ variant: "caption" }}
                            />
                          </ListItem>
                          {index < activityLogs.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                  </Box>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>,

    // Tab 1: Roles
    <React.Fragment key="roles">
      <Box sx={{ mt: 1, ml: 2, mr: 2, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Role Management
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, mr: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Current Roles:
            </Typography>
            {(userData.group_names ?? []).length ? (
              (userData.group_names ?? []).map((name) => (
                <Chip key={name} label={name} variant="outlined" size="small" />
              ))
            ) : (
              <Chip label="None" variant="outlined" size="small" />
            )}
            {canEditUser && (
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveRoles}
                disabled={savingRoles}
                sx={{ ml: 2 }}
              >
                {savingRoles ? "Saving..." : "Save Roles"}
              </Button>
            )}
          </Box>
        </Box>
        <Grid container spacing={2}>
          {groups.map((role) => {
            const checked = selectedGroupIds.includes(role.id);

            return (
              <Grid size={{ xs: 12, sm: 6 }} key={role.id}>
                <Paper
                  onClick={
                    canEditUser ? () => handleToggleGroup(role.id) : undefined
                  }
                  sx={{
                    p: 2,
                    cursor: canEditUser ? "pointer" : "default",
                    border: checked ? 2 : 1,
                    borderColor: checked ? "primary.main" : "divider",
                    backgroundColor: checked ? "primary.50" : "grey.50",
                    "&:hover": {
                      bgcolor: checked ? "primary.100" : "grey.100",
                    },
                    transition: "all 0.2s ease-in-out",
                    opacity: checked ? 1 : 0.7,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={!canEditUser}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggleGroup(role.id)}
                      sx={{
                        mt: 0.5,
                        "&.Mui-checked": {
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: checked ? "primary.main" : "action.disabled",
                        },
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: checked ? "bold" : "medium",
                            color: checked ? "primary.main" : "text.secondary",
                          }}
                        >
                          {role.name}
                        </Typography>
                        {/* {checked && (
                        <Chip
                          label="Assigned"
                          size="small"
                          color="primary"
                          variant="filled"
                          sx={{
                            fontSize: "0.7rem",
                            height: 20,
                            fontWeight: "bold",
                          }}
                        />
                      )} */}
                      </Box>
                      {/* <Typography
                        variant="body2"
                        sx={{
                          color: checked ? "text.primary" : "text.secondary",
                        }}
                      >
                        {role.description}
                      </Typography> */}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </React.Fragment>,

    // Tab 2: Permissions
    <React.Fragment key="permissions">
      <Box sx={{ mt: 1, ml: 2, mr: 2, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Module Permissions
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {<Chip label={userData.role} color="primary" size="small" />}
          </Box>
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search permissions by module..."
            value={permissionSearch}
            onChange={(e) => setPermissionSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.disabled" }} />
              ),
            }}
            sx={{ maxWidth: 360 }}
          />
        </Box>
        <div style={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={filteredModulePermissions}
            columns={permission_columns}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25, page: 0 },
              },
            }}
            disableRowSelectionOnClick
            density="compact"
          />
        </div>

        <Box sx={{ mt: 3, p: 2, backgroundColor: "#f8f9fa", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> These are the user's effective permissions,
            resolved from their assigned roles (groups) plus any directly
            granted permissions.
          </Typography>
        </Box>
      </Box>
    </React.Fragment>,

    // Tab 3: Professional Development
    <ProfessionalDevelopmentTab
      key="professional_development"
      employmentHistory={employmentHistory}
      setEmploymentHistory={setEmploymentHistory}
      courses={courses}
      setCourses={setCourses}
      tShirtSize={tShirtSize}
      setTShirtSize={setTShirtSize}
      clothingSizes={clothingSizes}
      setClothingSizes={setClothingSizes}
    />,

    // Tab 4: Employment History Calendar
    <EmploymentHistoryCalendar
      key="employment_history_calendar"
      employees={[
        {
          id: userData?.id || 0,
          name: `${userData?.first_name} ${userData?.last_name}`,
          employmentHistory: employmentHistory,
        },
      ]}
      singleUserView={true}
    />,

    // Tab 5: 16 Personalities
    <PersonalityTestTab
      key="personality_test"
      personalityTest={personalityTest}
      setPersonalityTest={setPersonalityTest}
    />,

    // Tab 6: Activity Logs
    <React.Fragment key="activity_logs">
      <Box sx={{ mt: 1, ml: 2, mr: 2, py: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
          Activity Logs
        </Typography>

        {/* {timeFilter === "custom" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <DatePicker
                label="From"
                value={customFromDate}
              // onChange={(newValue) => setCustomFromDate(newValue)}
              />

              <DatePicker
                label="To"
                value={customToDate}
              // onChange={(newValue) => setCustomToDate(newValue)}
              />
            </Stack>
          </LocalizationProvider>
        )} */}

        <Paper sx={{ mt: 2 }}>
          {/* FILTER CONTROLS */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
            <TextField
              select
              label="Time Filter"
              size="small"
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
              sx={{ width: 200 }}
            >
              {" "}
              <MenuItem value="all">All Time Logs</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="last_3_days">Last 3 Days</MenuItem>
              <MenuItem value="this_week">This Week</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
            </TextField>

            <TextField
              select
              label="Activity"
              size="small"
              value={activityFilter}
              onChange={(e) =>
                setActivityFilter(
                  e.target.value as
                    | "All"
                    | "create"
                    | "update"
                    | "delete"
                    | "view",
                )
              }
              sx={{ width: 200 }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="create">Create</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
              <MenuItem value="view">View</MenuItem>
            </TextField>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredActivityLogs}
              getRowId={(row) => row.id}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
            />
          )}
        </Paper>
      </Box>
    </React.Fragment>,
  ];

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box
        sx={{ mb: 3, backgroundColor: colors.primary[900], borderRadius: 1 }}
      >
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid size={{ xs: 12, md: 12 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <EditAvatar
                  pilot={{
                    id: userData.id,
                    name: userData.first_name + " " + userData.last_name,
                    email: userData.email,
                  }}
                  initialImage={userData.profile_picture_url || undefined}
                  fetchMember={() => {
                    fetchUserById();
                  }}
                />
                {/* <Avatar sx={{ width: 80, height: 80, fontSize: "1.5rem" }} src={userData.profile_picture_url}>
                  {!userData?.profile_picture_url && <PersonIcon sx={{ fontSize: 48, color: 'grey.100' }} />}
                </Avatar> */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold">
                      {userData.first_name} {userData.last_name}
                    </Typography>
                    <Chip
                      label={userData.is_active ? "Active" : "Suspended"}
                      color={getStatusColor(
                        userData.is_active ? "Active" : "Suspended",
                      )}
                      size="small"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {
                      <Chip
                        label={userData.role.toUpperCase() || "N/A"}
                        color="primary"
                        size="small"
                      />
                    }
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    <PersonIcon fontSize="small" />
                    <Typography variant="body2">
                      {isCrewMember
                        ? "Also a crew member"
                        : "Not a crew member"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Last Login: {lastLogIn}
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 2,
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* <LinkIcon fontSize="small" color="disabled" />
                 */}

                {isCrewMember ? (
                  <Link
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/crew-management/view/${crew_member_id}`);
                    }}
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      ml: 1,
                    }}
                  >
                    View Crew Profile
                    <OpenInNewIcon fontSize="small" />
                  </Link>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setAddMemberOpen(true)}
                  >
                    Add To Crew Members
                  </Button>
                )}
                {isReferralPartner ? (
                  <Link
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/referrals/view/${referralPartnerId}`);
                    }}
                    sx={{ display: "flex", gap: 0.5, ml: 1 }}
                  >
                    View Referral Profile
                    <OpenInNewIcon fontSize="small" />
                  </Link>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setReferralModalOpen(true)}
                  >
                    Add as Referral Partner
                  </Button>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleEditUser}
                  startIcon={<EditIcon />}
                >
                  Edit User
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleResetPassword}
                  startIcon={<LockIcon />}
                >
                  Reset Password
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeactivateUser}
                  startIcon={<DeactivateIcon />}
                >
                  {userData.is_active ? "Deactivate" : "Activate"}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Roles" />
          <Tab label="Permissions" />
          <Tab label="Professional Development" />
          <Tab label="Employment History" />
          <Tab label="16 Personalities" />
          <Tab label="Activity Logs" />
        </Tabs>
      </Box>

      <Box sx={{ backgroundColor: colors.primary[900] }}>
        {tabContents[tabValue]}
      </Box>
      {userData && (
        <EditUserDialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          onSave={handleSaveUser}
          selectedUser={userData}
        />
      )}
      <AddCrewMemberDialog
        addMemberOpen={addMemberOpen}
        formData={formData}
        handleSelectChange={() => {}}
        user={addCrewMemberUser!}
        formLoading={formLoading}
        handleAddCrewMember={handleAddCrewMember}
        handleEmergencyContactChange={handleEmergencyContactChange}
        handleInputChange={handleInputChange}
        handlePhoneChange={handlePhoneChange}
        onCountryChange={onCountryChange}
        onRolesChange={onRolesChange}
        onSkillsChange={onSkillsChange}
        onToolsChange={onToolsChange}
        resetForm={resetForm}
        selectedCountry={selectedCountry}
        selectedRoles={selectedRoles}
        selectedSkills={selectedSkills}
        selectedTools={selectedTools}
        setAddMemberOpen={setAddMemberOpen}
        setFormData={setFormData}
      />
      <AddReferralPartnerModal
        open={referralModalOpen}
        onClose={() => setReferralModalOpen(false)}
        prefill={{
          name: userData.first_name ?? "",
          surname: userData.last_name ?? "",
          email: userData.email ?? "",
          phone: userData.phone_number ?? "",
        }}
        onSubmit={handleAddReferralPartner}
      />
    </Box>
  );
}
